// src/features/executive/performance/widgets/AvgRevenueTile.tsx
import { MenuItem, Select, Stack, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import KPICard from 'features/executive/components/KPICard/KPICard'
import KPIDetailRow from 'features/executive/components/KPICard/KPIDetailRow'
import { useAvgRevenuePerPatient } from '../hooks/use-avg-revenue-per-patient'
import { formatCurrency } from 'utils/executive.utils'
import type { ProcedureFilterMode } from 'api/executive/executive.types'

const STORAGE_KEY = 'executive.avgRevenue.procedureFilterMode'
const MODES: ProcedureFilterMode[] = ['all', 'include', 'exclude']

function readPersistedMode(): ProcedureFilterMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return MODES.includes(v as ProcedureFilterMode) ? (v as ProcedureFilterMode) : 'all'
  } catch {
    return 'all'
  }
}

export default function AvgRevenueTile() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<ProcedureFilterMode>(readPersistedMode)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      /* localStorage unavailable */
    }
  }, [mode])

  const { data, loading, error, refetch } = useAvgRevenuePerPatient(mode)

  const configured = data?.procedureFilterConfigured === true
  const effectiveMode: ProcedureFilterMode = configured ? mode : 'all'

  // Z.13 — collapse the previous two-stage empty (no-data + zero-patients-in-all-mode)
  // into a single flag computed up-front, so the precedence is uniform with the
  // other tiles.
  const isEmpty = !data || (data.patients === 0 && effectiveMode === 'all')

  const variance =
    data && data.previousAvgRevenue != null && data.previousAvgRevenue > 0
      ? (data.avgRevenue - data.previousAvgRevenue) / data.previousAvgRevenue
      : null

  const medianVariance =
    data && data.previousMedianRevenue != null && data.previousMedianRevenue > 0
      ? (data.medianRevenue - data.previousMedianRevenue) / data.previousMedianRevenue
      : null

  const filterTooltip = configured
    ? t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.FILTER_TOOLTIP', {
        count: data?.procedureTypeCount ?? 0,
      })
    : t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.FILTER_NOT_CONFIGURED')

  // Inline Select chrome — sized down to sit comfortably in the card header
  // without dominating it. Uses default MUI colors (legible on white card).
  const selectSx = {
    fontSize: '0.78rem',
    '& .MuiSelect-select': { py: 0.25, pr: '20px !important' },
  }

  const headerAction = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Tooltip
        title={filterTooltip}
        arrow
        placement="top"
        componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
      >
        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'help' }} />
      </Tooltip>
      <Select
        size="small"
        variant="standard"
        value={effectiveMode}
        onChange={(e) => setMode(e.target.value as ProcedureFilterMode)}
        sx={selectSx}
        inputProps={{ 'aria-label': t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.FILTER_ARIA') }}
      >
        <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>
          {t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.MODE_ALL', 'ทั้งหมด')}
        </MenuItem>
        <MenuItem value="include" disabled={!configured} sx={{ fontSize: '0.8rem' }}>
          {t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.MODE_INCLUDE', 'เฉพาะที่เลือก')}
        </MenuItem>
        <MenuItem value="exclude" disabled={!configured} sx={{ fontSize: '0.8rem' }}>
          {t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.MODE_EXCLUDE', 'ไม่รวมที่เลือก')}
        </MenuItem>
      </Select>
    </Stack>
  )

  return (
    <KPICard
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={140}
      title={t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.TITLE')}
      value={data ? formatCurrency(data.avgRevenue) : '—'}
      subtitle={
        data
          ? t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.PATIENTS_LABEL', {
              n: data.patients.toLocaleString('th-TH'),
            })
          : undefined
      }
      headerAction={headerAction}
      caption={
        data ? (
          <Stack spacing={0.5}>
            <KPIDetailRow
              label={t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.VS_PREV', 'vs งวดก่อน')}
              variance={variance}
            />
            <KPIDetailRow
              label={t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.MEDIAN_LABEL').replace(':', '')}
              labelTooltip={t('EXECUTIVE.PERFORMANCE.AVG_REVENUE.MEDIAN_TOOLTIP')}
              value={formatCurrency(data.medianRevenue)}
              variance={medianVariance}
            />
          </Stack>
        ) : undefined
      }
    />
  )
}
